import React, { useState } from 'react';
import { Table } from 'react-bootstrap';
import { OrdersData, OrderSourceLabel } from '../../types/IOrderResponse';
import TableFilters from '../OrderTableFilters';
import FilterBar, { FilterChip } from '../FilterBar';
import TablePagination, {
  PageSizeChoice,
  tableMinHeight,
} from '../TablePagination';
import TableStateRow from '../TableStateRow';
import DateTimeText from '../DateTimeText';
import StatusChip from '../StatusChip';
import getInitials from '../../utils/getInitials';
import formatPhone from '../../utils/formatPhone';
import { formatDateOnly } from '../../utils/formatOrderDate';
import useUrlFilters from '../../hooks/useUrlFilters';

interface IOrdersTableProps {
  data: OrdersData | undefined;
  email: string | undefined;
  setEmail: (email: string) => void;
  phone: string | undefined;
  setPhone: (phone: string) => void;
  itemsPerPage: number;
  handleModalOpen: (data: any) => void;
  currentPage: number;
  setCurrentPage: (page: number) => void;
  isFetching: boolean;
  setCustomerName: (s: any) => void;
  searchCustomerName: string | undefined;
  pid: string | undefined;
  setPid: (pid: string) => void;
  setKitters: (e: any) => void;
  setStatuses: (e: any) => void;
  setSources: (e: any) => void;
  selectedStatuses: string[] | undefined;
  selectedKitters: string[] | undefined;
  selectedSources: string[] | undefined;
  selectedDate: Date | Date[] | null | any;
  setSelectedDate: (e: any) => void;
  rowDetails: any;
  setRowDetails: any;
  onPageSizeChange?: (size: PageSizeChoice) => void;
  isAutoPageSize?: boolean;
  fitPageSize?: number;
  emptyMessage?: string;
}

const COLUMNS = 9;

const OrdersTable: React.FC<IOrdersTableProps> = ({
  data,
  handleModalOpen,
  itemsPerPage,
  currentPage,
  setCurrentPage,
  isFetching,
  setCustomerName,
  searchCustomerName,
  pid,
  setPid,
  setKitters,
  selectedKitters,
  setSources,
  selectedSources,
  setStatuses,
  selectedStatuses,
  selectedDate,
  setSelectedDate,
  rowDetails,
  setRowDetails,
  email,
  setEmail,
  phone,
  setPhone,
  onPageSizeChange,
  isAutoPageSize,
  fitPageSize,
  emptyMessage = 'No orders match these filters.',
}) => {
  const [clearSignal, setClearSignal] = useState(0);
  const [dateClearSignal, setDateClearSignal] = useState(0);
  // What is ticked inside the dropdowns before Apply is pressed.
  const [selectedStatusItems, setSelectedStatusItems] = useState<string[]>(
    selectedStatuses ?? [],
  );
  const [selectedKitterItems, setSelectedKitterItems] = useState<string[]>(
    selectedKitters ?? [],
  );
  const [selectedSourceItems, setSelectedSourceItems] = useState<string[]>(
    selectedSources ?? [],
  );

  useUrlFilters({
    page: { kind: 'page', value: currentPage, set: setCurrentPage },
    pid: { kind: 'text', value: pid, set: setPid },
    customerName: {
      kind: 'text',
      value: searchCustomerName,
      set: setCustomerName,
    },
    email: { kind: 'text', value: email, set: setEmail },
    phone: { kind: 'text', value: phone, set: setPhone },
    orderDate: {
      kind: 'text',
      value: typeof selectedDate === 'string' ? selectedDate : '',
      set: (next: string) => setSelectedDate(next || null),
    },
    status: {
      kind: 'list',
      value: selectedStatuses,
      set: (next: string[]) => {
        setStatuses(next);
        setSelectedStatusItems(next);
      },
    },
    kitter: {
      kind: 'list',
      value: selectedKitters,
      set: (next: string[]) => {
        setKitters(next);
        setSelectedKitterItems(next);
      },
    },
    source: {
      kind: 'list',
      value: selectedSources,
      set: (next: string[]) => {
        setSources(next);
        setSelectedSourceItems(next);
      },
    },
  });

  const resetFilters = () => {
    setPid('');
    setCustomerName('');
    setEmail('');
    setPhone('');
    setSelectedDate(null);
    setStatuses([]);
    setKitters([]);
    setSources([]);
    setSelectedStatusItems([]);
    setSelectedKitterItems([]);
    setSelectedSourceItems([]);
  };

  const dateLabel =
    typeof selectedDate === 'string' && selectedDate.includes('<>')
      ? selectedDate
          .split('<>')
          .map((d) => formatDateOnly(d.trim()))
          .join(' – ')
      : null;

  const chips: FilterChip[] = [];
  if (pid) {
    chips.push({
      key: 'pid',
      label: 'PID',
      value: pid,
      onClear: () => setPid(''),
    });
  }
  if (searchCustomerName) {
    chips.push({
      key: 'customer',
      label: 'Customer',
      value: searchCustomerName,
      onClear: () => setCustomerName(''),
    });
  }
  if (email) {
    chips.push({
      key: 'email',
      label: 'Email',
      value: email,
      onClear: () => setEmail(''),
    });
  }
  if (phone) {
    chips.push({
      key: 'phone',
      label: 'Phone',
      value: phone,
      onClear: () => setPhone(''),
    });
  }
  if (selectedSources?.length) {
    chips.push({
      key: 'source',
      label: 'Source',
      value: selectedSources.join(', '),
      onClear: () => {
        setSources([]);
        setSelectedSourceItems([]);
      },
    });
  }
  if (dateLabel) {
    chips.push({
      key: 'date',
      label: 'Order date',
      value: dateLabel,
      onClear: () => {
        setSelectedDate(null);
        setDateClearSignal((n) => n + 1);
      },
    });
  }
  if (selectedStatuses?.length) {
    chips.push({
      key: 'status',
      label: 'Status',
      value: selectedStatuses.join(', '),
      onClear: () => {
        setStatuses([]);
        setSelectedStatusItems([]);
      },
    });
  }
  if (selectedKitters?.length) {
    chips.push({
      key: 'kitter',
      label: 'Kitter',
      value: selectedKitters.join(', '),
      onClear: () => {
        setKitters([]);
        setSelectedKitterItems([]);
      },
    });
  }

  const hasRows = !!data?.items?.length;

  return (
    <div className="data-card">
      <FilterBar
        chips={chips}
        onClearAll={() => {
          resetFilters();
          setClearSignal((n) => n + 1);
        }}
      />
      <div
        className="table-scroll"
        style={{ minHeight: tableMinHeight(itemsPerPage, true) }}
      >
        <Table bordered={false} className="data-table orders-grid">
          <thead>
            <tr>
              <th>PID</th>
              <th>Customer</th>
              <th>Phone</th>
              <th className="cell-num">Kits</th>
              <th className="cell-num">Boxes</th>
              <th>Source</th>
              <th>Order date</th>
              <th>Status</th>
              <th>Kitter</th>
            </tr>
            <TableFilters
              selectedDate={selectedDate}
              setSelectedDate={setSelectedDate}
              searchCustomerName={searchCustomerName}
              setSearchCustomerName={setCustomerName}
              searchPid={pid}
              setSearchPid={setPid}
              statusItems={data?.selections?.Status || []}
              kitterItems={data?.selections?.Kitter || []}
              sourceItems={data?.selections?.Source || []}
              handleKitterApply={setKitters}
              handleSourceApply={setSources}
              selectedSourceItems={selectedSourceItems}
              setSelectedSourceItems={setSelectedSourceItems}
              handleStatusApply={setStatuses}
              handleClearFilters={resetFilters}
              selectedKitterItems={selectedKitterItems}
              selectedStatusItems={selectedStatusItems}
              setSelectedKitterItems={setSelectedKitterItems}
              setSelectedStatusItems={setSelectedStatusItems}
              email={email}
              setEmail={setEmail}
              phone={phone}
              setPhone={setPhone}
              clearSignal={clearSignal}
              dateClearSignal={dateClearSignal}
            />
          </thead>
          <tbody
            className={isFetching && hasRows ? 'is-refreshing' : undefined}
          >
            {!hasRows ? (
              <TableStateRow
                colSpan={COLUMNS}
                loading={isFetching}
                message={emptyMessage}
              />
            ) : (
              data!.items.map((order) => (
                <tr
                  role="button"
                  className={
                    rowDetails?.isActive && rowDetails.orderId === order.id
                      ? 'is-selected'
                      : ''
                  }
                  key={order.id}
                  data-track="order_row"
                  data-track-id={order.pid}
                  onClick={() => {
                    setRowDetails({
                      class: 'is-selected',
                      orderId: order.id,
                      isActive: true,
                    });
                    handleModalOpen({
                      modal: 'order',
                      data: { ...order },
                      isRow: true,
                    });
                  }}
                >
                  <td className="cell-pid">{order.pid}</td>
                  <td>
                    <div className="cell-customer">
                      <span className="cell-customer-name">
                        {order.customerName}
                      </span>
                      <span className="cell-customer-sub">
                        {order.emailAddress}
                      </span>
                    </div>
                  </td>
                  <td className="cell-mono muted">
                    {formatPhone(order.phoneNumber) || '—'}
                  </td>
                  <td className="cell-num">{order.numberOfKits}</td>
                  <td className="cell-num">{order.numberOfBoxes}</td>
                  <td>
                    <span className="source-tag">
                      {OrderSourceLabel[order.source]}
                    </span>
                  </td>
                  <td className="cell-mono">
                    <DateTimeText value={order.orderDate} />
                  </td>
                  <td>
                    <StatusChip status={order.status} />
                  </td>
                  <td>
                    {order.kitter ? (
                      <span className="cell-person">
                        <span className="avatar-sm">
                          {getInitials(order.kitter)}
                        </span>
                        {order.kitter}
                      </span>
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>
      <TablePagination
        currentPage={currentPage}
        totalPages={data?.maxPage}
        onPageChange={setCurrentPage}
        pageSize={itemsPerPage}
        onPageSizeChange={onPageSizeChange}
        isAutoPageSize={isAutoPageSize}
        fitPageSize={fitPageSize}
        total={data?.total}
        itemCount={data?.items?.length ?? 0}
        noun="orders"
      />
    </div>
  );
};

export default React.memo(OrdersTable);
